import { useMemo, useState } from 'react'
import { FormField } from '@/components/ui/form-field'
import { Input } from '@/components/ui/input'
import { NativeSelect } from '@/components/ui/native-select'
import { Textarea } from '@/components/ui/textarea'
import { useTeamMembers } from '@/features/preinvest/useTeamMembers'
import { shortDay } from '../../record/format'
import type { FollowUp, NewAgreement } from '../types'
import { FollowUpFields } from './FollowUpFields'
import { SheetFrame } from './SheetFrame'
import { followUpInput, useSchedulePreview } from './useSchedulePreview'

/** Edit a proposed new agreement: the fields of the agreement dialog, compact, with a live preview. */
export function NewAgreementSheet({
  agreement,
  onSave,
  onClose,
}: {
  agreement: NewAgreement
  onSave: (values: Record<string, unknown>) => void
  onClose: () => void
}) {
  const { membersById } = useTeamMembers()
  const [title, setTitle] = useState(agreement.title)
  const [owner, setOwner] = useState(agreement.owner_user_id ?? '')
  const [starts, setStarts] = useState(agreement.starts_on ?? '')
  const [ends, setEnds] = useState(agreement.ends_on ?? '')
  const [description, setDescription] = useState(agreement.description ?? '')
  const [follow, setFollow] = useState<FollowUp>(agreement.follow_up ?? { schedule: 'geen' })

  const request = useMemo(() => {
    const input = followUpInput(follow)
    if (!input || !(agreement.person_id || agreement.organisation_id)) return null
    return {
      ...input,
      title: title || 'Voorbeeld',
      person_id: agreement.person_id ?? null,
      organisation_id: agreement.organisation_id ?? null,
      owner_user_id: owner || null,
      starts_on: starts || null,
      ends_on: ends || null,
    }
  }, [follow, title, owner, starts, ends, agreement.person_id, agreement.organisation_id])
  const { preview, problem } = useSchedulePreview(request)

  return (
    <SheetFrame
      title="Nieuwe afspraak bewerken"
      onClose={onClose}
      saveDisabled={!title.trim()}
      onSave={() =>
        onSave({
          title: title.trim(),
          owner_user_id: owner || null,
          starts_on: starts || null,
          ends_on: ends || null,
          description: description.trim() || null,
          follow_up: follow.schedule ? follow : null,
        })
      }
    >
      <FormField label="Titel" required>
        <Input value={title} maxLength={200} onChange={(event) => setTitle(event.target.value)} />
      </FormField>
      <FormField label="Eigenaar" hint="Krijgt de taken.">
        <NativeSelect value={owner} onChange={(event) => setOwner(event.target.value)}>
          <option value="">Kies een eigenaar</option>
          {[...(membersById?.values() ?? [])].map((member) => (
            <option key={member.id} value={member.id}>
              {member.display_name}
            </option>
          ))}
        </NativeSelect>
      </FormField>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Start">
          <Input type="date" value={starts} onChange={(event) => setStarts(event.target.value)} />
        </FormField>
        <FormField label="Einde (optioneel)">
          <Input type="date" value={ends} onChange={(event) => setEnds(event.target.value)} />
        </FormField>
      </div>
      <FollowUpFields value={follow} onChange={setFollow} />
      {preview && preview.deadlines.length > 0 ? (
        <p className="text-sm" aria-live="polite">
          {`Volgende deadlines: ${preview.deadlines.map((day) => shortDay(day)).join(' · ')}`}
        </p>
      ) : null}
      {preview?.warnings.map((warning) => (
        <p key={warning} className="text-sm text-muted-foreground">
          {warning}
        </p>
      ))}
      {problem ? (
        <p role="alert" className="text-sm text-destructive">
          {problem}
        </p>
      ) : null}
      <FormField label="Omschrijving">
        <Textarea rows={3} value={description} onChange={(event) => setDescription(event.target.value)} />
      </FormField>
    </SheetFrame>
  )
}
